import type { OverviewMerchant } from "@unbox/api";
import { type MetricKey, type MetricReading, metricByKey, readMetric, sumValues } from "./metrics";
import { regionKey } from "./regions";

export const TABLE_METRICS = [
  "affiliateGmv",
  "videos",
  "views",
  "videoConversionRate",
  "activeCreators",
  "gmvPerVideo",
  "gmvPerSample",
] as const satisfies MetricKey[];

export type TableMetric = (typeof TABLE_METRICS)[number];

export const MONEY_METRICS: readonly TableMetric[] = ["affiliateGmv", "gmvPerVideo", "gmvPerSample"];

export type SortKey = TableMetric | "shareOfRegion" | "pendingReviews";

export type SortState = { key: SortKey; direction: "asc" | "desc" };

export const DEFAULT_SORT: SortState = { key: "affiliateGmv", direction: "desc" };

export type MerchantIdentity = { name: string; logoUrl: string | null };

export type MerchantRow = {
  merchantId: string;
  name: string;
  logoUrl: string | null;
  regionKey: string;
  currency: string;
  factor: number | null;
  shareOfRegion: number | null;
  pendingReviews: number | null;
  metrics: Record<TableMetric, MetricReading>;
};

export const buildMerchantRows = (
  merchants: OverviewMerchant[],
  identities: Map<string, MerchantIdentity>,
  factorOf: (currency: string) => number | null,
): MerchantRow[] => {
  const affiliateByRegion = new Map<string, number>();
  for (const merchant of merchants) {
    const key = regionKey(merchant.region);
    affiliateByRegion.set(key, (affiliateByRegion.get(key) ?? 0) + merchant.current.affiliateGmv);
  }

  return merchants.map((merchant) => {
    const key = regionKey(merchant.region);
    const current = sumValues([merchant.current]);
    const prior = sumValues([merchant.prior]);
    const regionTotal = affiliateByRegion.get(key) ?? 0;
    const identity = identities.get(merchant.merchantId);

    const metrics = Object.fromEntries(
      TABLE_METRICS.map((metricKey) => [metricKey, readMetric(metricByKey(metricKey), current, prior)]),
    ) as Record<TableMetric, MetricReading>;

    return {
      merchantId: merchant.merchantId,
      name: identity?.name ?? merchant.merchantId,
      logoUrl: identity?.logoUrl ?? null,
      regionKey: key,
      currency: merchant.currency,
      factor: factorOf(merchant.currency),
      shareOfRegion: regionTotal > 0 ? (merchant.current.affiliateGmv / regionTotal) * 100 : null,
      pendingReviews: merchant.pendingReviews,
      metrics,
    };
  });
};

export const convertedValue = (row: MerchantRow, key: TableMetric): number | null => {
  const value = row.metrics[key].value;
  if (value === null || !MONEY_METRICS.includes(key)) return value;
  return row.factor === null ? null : value * row.factor;
};

const sortValue = (row: MerchantRow, key: SortKey): number | null => {
  if (key === "shareOfRegion") return row.shareOfRegion;
  if (key === "pendingReviews") return row.pendingReviews;
  return convertedValue(row, key);
};

const compareDesc = (a: number | null, b: number | null): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
};

export const sortRows = (rows: MerchantRow[], sort: SortState): MerchantRow[] =>
  [...rows].sort((a, b) => {
    const left = sortValue(a, sort.key);
    const right = sortValue(b, sort.key);
    let primary = compareDesc(left, right);
    if (sort.direction === "asc" && left !== null && right !== null) primary = -primary;
    return primary || compareDesc(convertedValue(a, "affiliateGmv"), convertedValue(b, "affiliateGmv"));
  });

export const nextSort = (current: SortState, key: SortKey): SortState => {
  if (current.key !== key) return { key, direction: "desc" };
  const flipped = current.direction === "desc" ? "asc" : "desc";
  return { key, direction: flipped };
};
